import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Input } from './input';
import { Button } from './button';
import { Search } from 'lucide-react';

interface GifPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (gifUrl: string) => void;
}

export function GifPicker({ isOpen, onClose, onSelect }: GifPickerProps) {
  const [search, setSearch] = useState('');
  const [gifs, setGifs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const searchGifs = async () => {
    if (!search.trim()) return;

    setLoading(true);
    try {
      // Check if GIPHY API key is available
      const apiKey = import.meta.env.VITE_GIPHY_API_KEY;
      if (!apiKey) {
        console.warn('GIPHY API key not found');
        setGifs([]);
        setLoading(false);
        return;
      }

      const response = await fetch(
        `https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(
          search
        )}&limit=20`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch GIFs');
      }
      
      const data = await response.json();
      setGifs(data.data || []);
    } catch (error) {
      console.error('Error searching GIFs:', error);
      setGifs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-full mx-4 p-8 bg-gradient-to-br from-white via-purple-50/30 to-pink-50/20 backdrop-blur-xl border-0 shadow-2xl rounded-3xl">
        <DialogHeader className="text-center space-y-3 pb-6">
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-red-500 bg-clip-text text-transparent">
            Select a GIF
          </DialogTitle>
          <p className="text-gray-600">Find the perfect animated expression</p>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="flex gap-3 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search GIFs..."
                onKeyPress={(e) => e.key === 'Enter' && searchGifs()}
                className="pl-12 h-12 rounded-2xl border-0 bg-white/80 backdrop-blur-sm shadow-lg focus:shadow-xl transition-all duration-200 text-lg"
              />
            </div>
            <Button 
              onClick={searchGifs}
              className="h-12 px-6 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Search className="w-5 h-5" />
            </Button>
          </div>

          <div className="bg-white/80 rounded-2xl p-6 backdrop-blur-sm border border-white/20 shadow-lg">
            {loading ? (
              <div className="flex justify-center items-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-3 border-purple-600" />
                <span className="ml-4 text-lg text-gray-600">Searching for the perfect GIF...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {gifs.map((gif) => (
                  <div
                    key={gif.id}
                    className="cursor-pointer rounded-xl overflow-hidden hover:scale-105 transition-all duration-200 border-2 border-transparent hover:border-purple-200 hover:shadow-lg group"
                    onClick={() => {
                      onSelect(gif.images.original.url);
                      onClose();
                    }}
                  >
                    <img
                      src={gif.images.fixed_height.url}
                      alt={gif.title}
                      className="w-full h-auto group-hover:scale-110 transition-transform duration-200"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}